import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment, { Moment } from 'moment';
import { Box, Stack, TextField, Typography } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import IconButton from '@mui/material/IconButton';
import { Button } from '../../../../mui-builder';

interface TimePickerProps {
  date: Moment;
  setDate: (date: Moment) => void;
  onBackToCalendar?: () => void;
}

interface TimeCounterProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  ariaLabel: string;
}

const TimeCounter: React.FC<TimeCounterProps> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  onIncrement,
  onDecrement,
  inputRef,
  ariaLabel,
}) => (
  <Stack alignItems="center" spacing={0.5}>
    <IconButton
      size="small"
      onClick={onIncrement}
      sx={{
        color: 'text.primary',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <KeyboardArrowUpIcon />
    </IconButton>

    <TextField
      inputRef={inputRef}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      variant="outlined"
      slotProps={{
        input: {
          inputProps: {
            maxLength: 2,
            'aria-label': ariaLabel,
          },
        },
      }}
      sx={{
        width: 52,
        '& .MuiOutlinedInput-root': {
          height: 44,
          '& input': {
            textAlign: 'center',
            padding: '8px 4px',
            fontSize: '1.25rem',
            fontWeight: 500,
          },
          '& fieldset': {
            borderWidth: 1,
          },
          '&:hover fieldset': {
            borderColor: 'primary.main',
          },
          '&.Mui-focused fieldset': {
            borderWidth: 1.5,
            borderColor: 'primary.main',
          },
        },
      }}
    />

    <IconButton
      size="small"
      onClick={onDecrement}
      sx={{
        color: 'text.primary',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <KeyboardArrowDownIcon />
    </IconButton>
  </Stack>
);

export const CustomTimePicker: React.FC<TimePickerProps> = ({ date, setDate, onBackToCalendar }) => {
  const currentMoment = moment.isMoment(date) ? date : moment(date);

  const [hoursInput, setHoursInput] = useState(() => {
    return currentMoment.hours().toString().padStart(2, '0');
  });
  const [minutesInput, setMinutesInput] = useState(() => currentMoment.minutes().toString().padStart(2, '0'));

  const hoursInputRef = useRef<HTMLInputElement>(null);
  const minutesInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (moment.isMoment(date)) {
      setHoursInput(date.hours().toString().padStart(2, '0'));
      setMinutesInput(date.minutes().toString().padStart(2, '0'));
    }
  }, [date]);

  const updateTime = useCallback(
    (hours: number, minutes: number) => {
      const updated = currentMoment.clone().hours(hours).minutes(minutes).seconds(0).milliseconds(0);
      setDate(updated);
    },
    [currentMoment, setDate],
  );

  const handleHoursArrow = (delta: number) => {
    const currentHours = currentMoment.hours();
    let newHours = currentHours + delta;

    if (newHours < 0) newHours = 23;
    if (newHours > 23) newHours = 0;

    const updated = currentMoment.clone().hours(newHours).seconds(0).milliseconds(0);
    setDate(updated);
  };

  const handleMinutesArrow = (delta: number) => {
    const currentMinutes = currentMoment.minutes();
    let newMinutes = currentMinutes + delta;

    if (newMinutes < 0) newMinutes = 59;
    if (newMinutes > 59) newMinutes = 0;

    const updated = currentMoment.clone().minutes(newMinutes).seconds(0).milliseconds(0);
    setDate(updated);
  };

  const handleHoursInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 2) {
      setHoursInput(value);
    }
  };

  const handleHoursInputBlur = () => {
    let num = parseInt(hoursInput, 10);
    if (isNaN(num)) num = 0;

    if (num < 0) num = 0;
    if (num > 23) num = 23;
    setHoursInput(num.toString().padStart(2, '0'));
    updateTime(num, currentMoment.minutes());
  };

  const handleHoursKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleHoursInputBlur();
      minutesInputRef.current?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleHoursArrow(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleHoursArrow(-1);
    }
  };

  const handleMinutesInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 2) {
      setMinutesInput(value);
    }
  };

  const handleMinutesInputBlur = () => {
    let num = parseInt(minutesInput, 10);
    if (isNaN(num)) num = 0;
    if (num < 0) num = 0;
    if (num > 59) num = 59;

    setMinutesInput(num.toString().padStart(2, '0'));
    updateTime(currentMoment.hours(), num);
  };

  const handleMinutesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleMinutesInputBlur();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleMinutesArrow(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleMinutesArrow(-1);
    }
  };

  return (
    <Box sx={{ py: 1 }}>
      {onBackToCalendar && (
        <Box sx={{ mb: 0.5, textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={onBackToCalendar}
            className="rdtTimeToggle"
            sx={{
              color: 'text.primary',
              width: '100%',
            }}
          >
            {currentMoment.format('LL')}
          </Button>
        </Box>
      )}

      {/* Time counters */}
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ py: 1 }}>
        {/* Hours */}
        <TimeCounter
          value={hoursInput}
          onChange={handleHoursInputChange}
          onBlur={handleHoursInputBlur}
          onKeyDown={handleHoursKeyDown}
          onIncrement={() => handleHoursArrow(1)}
          onDecrement={() => handleHoursArrow(-1)}
          inputRef={hoursInputRef}
          ariaLabel="Hours"
        />
        <Typography variant="h3" fontWeight={700}>
          :
        </Typography>

        {/* Minutes */}
        <TimeCounter
          value={minutesInput}
          onChange={handleMinutesInputChange}
          onBlur={handleMinutesInputBlur}
          onKeyDown={handleMinutesKeyDown}
          onIncrement={() => handleMinutesArrow(1)}
          onDecrement={() => handleMinutesArrow(-1)}
          inputRef={minutesInputRef}
          ariaLabel="Minutes"
        />
      </Stack>
    </Box>
  );
};
